'use client'

/**
 * useAshraryaSession — per-user conversation persistence.
 *
 * Responsibilities:
 *   - Generate (or restore) a stable `session_id` per (appNamespace, userId)
 *   - Persist conversation history in localStorage (capped at 50 messages)
 *   - Expose `addMessage` and `clearSession` for the ChatPanel
 *
 * The hook intentionally does ONE thing: client-side memory. The n8n backend
 * is the source of truth for full history; this is just so the user can close
 * and reopen the panel without losing context within a single browser.
 */

import { useEffect, useState, useCallback, useRef } from 'react'

const SESSION_PREFIX = 'ashraya:session:'
const HISTORY_PREFIX = 'ashraya:history:'
const MAX_HISTORY = 50

// RFC4122 v4 UUID. Falls back to a Math.random-based implementation on
// browsers without crypto.randomUUID (older Safari).
function uuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function safeGet(key) {
  try { return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null } catch { return null }
}
function safeSet(key, value) {
  try { if (typeof window !== 'undefined') window.localStorage.setItem(key, value) } catch {}
}
function safeRemove(key) {
  try { if (typeof window !== 'undefined') window.localStorage.removeItem(key) } catch {}
}

export function useAshraryaSession({ appNamespace, userId } = {}) {
  const ns = String(appNamespace || 'unknown').toLowerCase()
  const uid = String(userId || 'anon')
  const sessionKey = `${SESSION_PREFIX}${ns}:${uid}`
  const historyKey = `${HISTORY_PREFIX}${ns}:${uid}`

  const [sessionId, setSessionId] = useState(null)
  const [history, setHistory] = useState([])
  const readyRef = useRef(false)

  // Hydrate from localStorage once on mount
  useEffect(() => {
    if (typeof window === 'undefined') return
    const hydrate = () => {
      let sid = safeGet(sessionKey)
      if (!sid) {
        sid = uuid()
        safeSet(sessionKey, sid)
      }
      setSessionId(sid)

      const raw = safeGet(historyKey)
      if (raw) {
        try {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) {
            setHistory(parsed.slice(-MAX_HISTORY))
          } else {
            setHistory([])
          }
        } catch {
          // Corrupted history — start fresh rather than crash
          safeRemove(historyKey)
          setHistory([])
        }
      } else {
        setHistory([])
      }
      readyRef.current = true
    }
    hydrate()
    // Listen for provider-driven session resets (e.g. after a tour completes
    // the provider wipes session+history so the next open shows the welcome
    // screen — if ChatPanel happens to be mounted at that moment we need to
    // re-hydrate from the now-empty storage rather than keep showing stale
    // in-memory history).
    const onReset = () => hydrate()
    window.addEventListener('ashraya:session-reset', onReset)
    return () => window.removeEventListener('ashraya:session-reset', onReset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey, historyKey])

  const addMessage = useCallback((msg) => {
    setHistory((prev) => {
      const enriched = { ...msg, id: msg.id || uuid(), ts: msg.ts || Date.now() }
      const next = [...prev, enriched].slice(-MAX_HISTORY)
      safeSet(historyKey, JSON.stringify(next))
      return next
    })
  }, [historyKey])

  const clearSession = useCallback(() => {
    const sid = uuid()
    setSessionId(sid)
    setHistory([])
    safeSet(sessionKey, sid)
    safeRemove(historyKey)
  }, [sessionKey, historyKey])

  return { sessionId, history, addMessage, clearSession, ready: readyRef.current }
}
